import { Row } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { Checkbox } from '../../Checkbox';
import { CHECKBOX_COLUMN_WIDTH } from '../constants';
import { ColumnGroup, RowSelectionConfig } from '../types';

function flattenNestedRowsData<TData extends { children?: TData[] }>(
  data: TData[]
): TData[] {
  if (!data) return [];

  return data.reduce<TData[]>((acc, item) => {
    acc.push(item);
    if (item.children) {
      acc.push(...flattenNestedRowsData(item.children));
    }
    return acc;
  }, []);
}

function areAllRowsSelected<TData>(
  selectedRowIds: Set<string>,
  fullData: TData[],
  getRowId: (row: TData) => string
) {
  return (
    fullData.every((row) => selectedRowIds.has(getRowId(row))) &&
    fullData.length > 0
  );
}

function toggleRowSelectionForChildren<TData extends { children?: TData[] }>(
  row: TData,
  selectedRowIds: Set<string>,
  getRowId: (row: TData) => string,
  shouldSelect: boolean
) {
  if (!row.children) return;

  const stack = [...row.children];
  while (stack.length > 0) {
    const child = stack.pop();
    if (!child) {
      break;
    }
    const childId = getRowId(child);
    if (shouldSelect) {
      selectedRowIds.add(childId);
    } else {
      selectedRowIds.delete(childId);
    }
    if (child.children) {
      stack.push(...child.children);
    }
  }
}

export function useRowSelection<TData extends { children?: TData[] }>(
  data: TData[],
  rowSelectionConfig: RowSelectionConfig<TData>
): {
  column: ColumnGroup<TData, unknown>;
  selectedRows: TData[];
  unselectAllRows: () => void;
  // Expose selection API so checkbox UI can be embedded in first column
  isRowSelectedFn: (row: TData) => boolean;
  toggleRowById: (rowId: string) => void;
  toggleAll: (rowsSubset?: TData[]) => void;
  getRowIdInternal: (row: TData) => string;
} {
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(
    new Set(rowSelectionConfig.initialSelectedRows || [])
  );
  const [selectedRows, setSelectedRows] = useState<TData[]>([]);
  const allFlattenedRows = flattenNestedRowsData(data);

  const getRowId = (row: TData): string => {
    return (
      rowSelectionConfig.getRowId?.(row) ||
      (row as unknown as { id?: string }).id ||
      ''
    );
  };

  // Check if a row is selected, use external isRowSelected if provided
  const isRowSelected = (row: TData): boolean => {
    if (rowSelectionConfig.isRowSelected) {
      return rowSelectionConfig.isRowSelected(row);
    }
    return selectedRowIds.has(getRowId(row));
  };

  const rowCheckedState = (row: TData): boolean | 'indeterminate' => {
    if (isRowSelected(row)) return true;
    return rowSelectionConfig.isRowIndeterminate?.(row)
      ? 'indeterminate'
      : false;
  };

  // Update selectedRows whenever selectedRowIds changes or when using external isRowSelected
  useEffect(() => {
    if (rowSelectionConfig.isRowSelected) {
      // If using external isRowSelected, keep selectedRows in sync
      const newSelectedRows = allFlattenedRows.filter((row) =>
        isRowSelected(row)
      );
      setSelectedRows(newSelectedRows);
    } else {
      // Use internal selection state
      const newSelectedRows = allFlattenedRows.filter((row) =>
        selectedRowIds.has(getRowId(row))
      );
      setSelectedRows(newSelectedRows);
    }
  }, [data, selectedRowIds, rowSelectionConfig.isRowSelected]);

  const handleRowSelection = (rowId: string) => {
    if (rowSelectionConfig.isRowSelected) {
      // If using external isRowSelected, we need a different approach
      const targetRow = allFlattenedRows.find((row) => getRowId(row) === rowId);
      if (!targetRow) return;

      const isCurrentlySelected = isRowSelected(targetRow);
      const currentSelectedRows = allFlattenedRows.filter((row) =>
        isRowSelected(row)
      );

      if (rowSelectionConfig.selectionMode === 'single') {
        // For single selection mode
        const newSelectedRows = isCurrentlySelected ? [] : [targetRow];
        rowSelectionConfig.onSelectionChange?.(newSelectedRows);
        return;
      }

      // For multiple selection with children
      // First, create a set of currently selected IDs for efficiency
      const currentSelectedIds = new Set(
        currentSelectedRows.map((row) => getRowId(row))
      );

      // Toggle the target row
      if (isCurrentlySelected) {
        currentSelectedIds.delete(rowId);

        // Also deselect children if applicable
        if (targetRow.children) {
          const childrenToDeselect = flattenNestedRowsData(
            targetRow.children as TData[]
          );
          childrenToDeselect.forEach((child) => {
            currentSelectedIds.delete(getRowId(child));
          });
        }
      } else {
        currentSelectedIds.add(rowId);

        // Also select children if applicable
        if (targetRow.children) {
          const childrenToSelect = flattenNestedRowsData(
            targetRow.children as TData[]
          );
          childrenToSelect.forEach((child) => {
            currentSelectedIds.add(getRowId(child));
          });
        }
      }

      // Build new selected rows array
      const newSelectedRows = allFlattenedRows.filter((row) =>
        currentSelectedIds.has(getRowId(row))
      );

      rowSelectionConfig.onSelectionChange?.(newSelectedRows);
    } else {
      // Using internal selection state - original logic
      const newSelectedRowIds = new Set(selectedRowIds);

      if (rowSelectionConfig.selectionMode === 'single') {
        newSelectedRowIds.clear();
        newSelectedRowIds.add(rowId);
        setSelectedRowIds(newSelectedRowIds);

        const newSelectedRows = allFlattenedRows.filter((row) =>
          newSelectedRowIds.has(getRowId(row))
        );

        setSelectedRows(newSelectedRows);
        rowSelectionConfig.onSelectionChange?.(newSelectedRows);
        return;
      }

      const targetRow = allFlattenedRows.find((row) => getRowId(row) === rowId);
      if (!targetRow) return;

      if (newSelectedRowIds.has(rowId)) {
        newSelectedRowIds.delete(rowId);
        toggleRowSelectionForChildren(
          targetRow,
          newSelectedRowIds,
          getRowId,
          false
        );
      } else {
        newSelectedRowIds.add(rowId);
        toggleRowSelectionForChildren(
          targetRow,
          newSelectedRowIds,
          getRowId,
          true
        );
      }

      setSelectedRowIds(newSelectedRowIds);

      const newSelectedRows = allFlattenedRows.filter((row) =>
        newSelectedRowIds.has(getRowId(row))
      );

      setSelectedRows(newSelectedRows);
      rowSelectionConfig.onSelectionChange?.(newSelectedRows);
    }
  };

  const handleSelectAllRows = (rowsSubset?: TData[]) => {
    // Check if all rows are currently selected
    const rowsInScope = rowsSubset
      ? flattenNestedRowsData(rowsSubset as TData[])
      : allFlattenedRows;

    const allSelected = rowSelectionConfig.isRowSelected
      ? rowsInScope.length > 0 && rowsInScope.every((row) => isRowSelected(row))
      : areAllRowsSelected(selectedRowIds, rowsInScope, getRowId);

    if (rowSelectionConfig.isRowSelected) {
      // Using external isRowSelected
      if (allSelected) {
        // Deselect all
        if (rowsSubset) {
          const currentSelected = allFlattenedRows.filter((row) =>
            isRowSelected(row)
          );
          const subsetIds = new Set(rowsInScope.map(getRowId));
          const remaining = currentSelected.filter(
            (r) => !subsetIds.has(getRowId(r))
          );
          rowSelectionConfig.onSelectionChange?.(remaining);
        } else {
          rowSelectionConfig.onSelectionChange?.([]);
        }
      } else if (rowsSubset) {
        // Select all (subset): merge with existing
        const currentSelected = new Map(
          allFlattenedRows
            .filter((row) => isRowSelected(row))
            .map((r) => [getRowId(r), r])
        );
        rowsInScope.forEach((r) => currentSelected.set(getRowId(r), r));
        rowSelectionConfig.onSelectionChange?.(
          Array.from(currentSelected.values())
        );
      } else {
        rowSelectionConfig.onSelectionChange?.(allFlattenedRows);
      }
    } else {
      // Using internal selection state
      if (!allSelected) {
        const newSelectedRowIds = new Set(
          (rowsSubset ? rowsInScope : allFlattenedRows).map(getRowId)
        );
        if (rowsSubset) {
          // merge with existing selections outside the subset
          selectedRowIds.forEach((id) => newSelectedRowIds.add(id));
        }
        setSelectedRowIds(newSelectedRowIds);
        const newSelectedRows = allFlattenedRows.filter((row) =>
          newSelectedRowIds.has(getRowId(row))
        );
        setSelectedRows(newSelectedRows);
        rowSelectionConfig.onSelectionChange?.(newSelectedRows);
        return;
      }

      if (rowsSubset) {
        const newSelected = new Set(selectedRowIds);
        rowsInScope.forEach((r) => newSelected.delete(getRowId(r)));
        setSelectedRowIds(newSelected);
        const newSelectedRows = allFlattenedRows.filter((row) =>
          newSelected.has(getRowId(row))
        );
        setSelectedRows(newSelectedRows);
        rowSelectionConfig.onSelectionChange?.(newSelectedRows);
      } else {
        setSelectedRowIds(new Set());
        setSelectedRows([]);
        rowSelectionConfig.onSelectionChange?.([]);
      }
    }
  };

  const unselectAllRows = () => {
    if (rowSelectionConfig.isRowSelected) {
      // Using external isRowSelected
      rowSelectionConfig.onSelectionChange?.([]);
    } else {
      // Using internal selection state
      setSelectedRowIds(new Set());
      setSelectedRows([]);
      rowSelectionConfig.onSelectionChange?.([]);
    }
  };

  return {
    column: {
      id: 'selection',
      sticky: 'left',
      columns: [
        {
          id: 'select',
          accessorKey: 'select',
          header: () => (
            <div className="checkbox-cell">
              <Checkbox
                checked={
                  rowSelectionConfig.isRowSelected
                    ? allFlattenedRows.length > 0 &&
                      allFlattenedRows.every((row) => isRowSelected(row))
                    : areAllRowsSelected(
                        selectedRowIds,
                        allFlattenedRows,
                        getRowId
                      )
                }
                onCheckedChange={() => handleSelectAllRows()}
                aria-label="Select all"
              />
            </div>
          ),
          cell: ({ row }: { row: Row<TData> }) => (
            <div className="checkbox-cell">
              <Checkbox
                checked={rowCheckedState(row.original)}
                onCheckedChange={() =>
                  handleRowSelection(getRowId(row.original))
                }
                aria-label="Select row"
              />
            </div>
          ),
          width: CHECKBOX_COLUMN_WIDTH,
          enableSorting: false,
          showInSelector: false,
        },
      ],
    },
    selectedRows,
    unselectAllRows,
    isRowSelectedFn: isRowSelected,
    toggleRowById: handleRowSelection,
    toggleAll: handleSelectAllRows,
    getRowIdInternal: getRowId,
  };
}
